import React from 'react';
import {createPortal} from 'react-dom';
import {Toast, ToastPlacement, ToastSkin} from 'wix-ui-tpa';
import {Spinner} from 'wix-ui-tpa/cssVars';
import {TranslationMap} from '../../../types/common';
import {FlagIcon} from '../../../commons/components/FlagIcon/FlagIcon';
import {useCountryInfoHook} from '../../hooks/useCountryInfoHook';
import {toastClassName, toastSpinnerClassName, toastStyleOverrides, toastStyles} from './AutoSwitchToast.styles';

export type AutoSwitchToastPhase = 'loading' | 'undo';

const canUseDOM = typeof document !== 'undefined';

const getTranslation = (translations: TranslationMap, key: string) => translations?.[key] ?? '';
const withCurrency = (template: string, currency: string) => template.replace('{currency}', currency);

export interface AutoSwitchToastProps {
  currency: string | null;
  phase: AutoSwitchToastPhase;
  previousCurrency: string | null;
  translations: TranslationMap;
  onClose: () => void;
  onUndo: () => void;
}

export const AutoSwitchToast = ({
  currency,
  phase,
  previousCurrency,
  translations,
  onClose,
  onUndo,
}: AutoSwitchToastProps) => {
  const {userCountryInfo} = useCountryInfoHook(currency ?? '');

  if (!canUseDOM || !currency) {
    return null;
  }

  const undoLabel = getTranslation(translations, 'currencyConverter.toast.autoSwitched.undo');
  const loadingMessage = getTranslation(translations, 'currencyConverter.toast.autoSwitched.loading');
  const successMessage = getTranslation(translations, 'currencyConverter.toast.autoSwitched.success');
  const loadingAriaLabel = getTranslation(translations, 'currencyConverter.toast.autoSwitched.loadingAriaLabel');
  const message = withCurrency(phase === 'loading' ? loadingMessage : successMessage, currency);

  return createPortal(
    <div style={toastStyles.portal} id="auto-switched-toast">
      <style>{toastStyleOverrides}</style>
      <Toast
        skin={ToastSkin.status}
        placement={ToastPlacement.inline}
        isShown={!!currency}
        shouldAnimate
        shouldShowCloseButton={phase === 'undo'}
        onClose={onClose}
        className={toastClassName}
        data-hook={`auto-switched-toast-${phase}`}>
        <span style={toastStyles.content}>
          <FlagIcon countryCode={userCountryInfo?.flagCountryCode} size={22} />
          <span style={toastStyles.message}>{message}</span>
          <span style={toastStyles.actions}>
            {phase === 'loading' && (
              <Spinner diameter={18} className={toastSpinnerClassName} aria-label={loadingAriaLabel} />
            )}
            {phase === 'undo' && previousCurrency && (
              <button
                type="button"
                data-hook="auto-switched-toast-undo"
                onClick={onUndo}
                style={toastStyles.undoButton}>
                {withCurrency(undoLabel, previousCurrency)}
              </button>
            )}
          </span>
        </span>
      </Toast>
    </div>,
    document.body
  );
};
